import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { catchError, concatMap, of, Subject, take, tap } from 'rxjs';

import { DEFAULT_USER_SETTINGS, isTheme, type Theme, type UserSettings } from '@demeter/lib/models';
import { CoreApiService } from '@demeter/modules/api/core-api.service';
import { AuthService } from '@demeter/modules/auth';

export type SettingsState = 'loading' | 'loaded' | 'unavailable';
export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const THEME_ECHO_KEY = 'theme';
const THEME_SETTING_KEY = 'theme-setting';

@Injectable({ providedIn: 'root' })
export class UserSettingsService {
	private readonly api = inject(CoreApiService);
	private readonly auth = inject(AuthService);

	private readonly _settings = signal<UserSettings>(seedInitialSettings());
	private readonly _state = signal<SettingsState>('loading');
	private readonly _saveState = signal<SaveState>('idle');
	private readonly _systemDark = signal(false);
	private readonly _themeChanged = signal(0);
	private readonly _queue = new Subject<{ patch: Partial<UserSettings>; before: UserSettings }>();
	private loaded = false;

	readonly settings = this._settings.asReadonly();
	readonly state = this._state.asReadonly();
	readonly saveState = this._saveState.asReadonly();
	readonly themeChanged = this._themeChanged.asReadonly();
	readonly resolvedTheme = computed<'light' | 'dark'>(() => {
		const t = this._settings().theme;
		return t === 'system' ? (this._systemDark() ? 'dark' : 'light') : t;
	});

	constructor() {
		const mql = window.matchMedia('(prefers-color-scheme: dark)');
		this._systemDark.set(mql.matches);
		mql.addEventListener('change', e => this._systemDark.set(e.matches));

		// `before` is captured at ENQUEUE time (in save()), not here at dequeue — by the time a
		// buffered patch is dequeued, later optimistic updates may have already landed on
		// _settings(), so recomputing "before" here would revert to the wrong snapshot.
		this._queue
			.pipe(
				concatMap(({ patch, before }) => {
					this._saveState.set('saving');
					return this.api.saveUserSettings(patch).pipe(
						take(1),
						tap(() => this._saveState.set('saved')),
						catchError(() => {
							this._settings.set({ ...this._settings(), ...revert(before, patch) });
							this._saveState.set('error');
							return of(null);
						})
					);
				})
			)
			.subscribe();

		effect(() => {
			const resolved = this.resolvedTheme();
			document.documentElement.setAttribute('data-bs-theme', resolved);
			this._themeChanged.update(n => n + 1);
			try {
				localStorage.setItem(THEME_ECHO_KEY, resolved);
			} catch {
				/* private mode / storage evicted — the boot hint is a convenience only */
			}
		});

		// Separate from the resolved-value echo above: index.html's boot script and this effect's
		// own echo only ever need 'light'/'dark', but seedInitialSettings() needs the ACTUAL theme
		// setting (which may be 'system') so a session where load() never resolves — signed out,
		// the latched 'failed' auth status, or a flags-fetch leaving state 'unavailable' — reseeds
		// the user's real choice instead of silently pinning them to whichever theme 'system'
		// happened to resolve to at last boot.
		effect(() => {
			const theme = this._settings().theme;
			try {
				localStorage.setItem(THEME_SETTING_KEY, theme);
			} catch {
				/* private mode / storage evicted — the boot hint is a convenience only */
			}
		});
	}

	load(): void {
		if (this.loaded || this.auth.status() !== 'authenticated') {
			return;
		}
		this.loaded = true;
		this.api
			.getUserSettings()
			.pipe(take(1))
			.subscribe({
				next: s => {
					this._settings.set({
						theme: isTheme(s?.theme) ? s.theme : DEFAULT_USER_SETTINGS.theme,
						presentationMode:
							s?.presentationMode ?? DEFAULT_USER_SETTINGS.presentationMode
					});
					this._state.set('loaded');
				},
				error: () => this._state.set('unavailable')
			});
	}

	save(patch: Partial<UserSettings>): void {
		if (this._state() === 'unavailable') {
			return;
		}
		const before = this._settings();
		this._settings.update(s => ({ ...s, ...patch }));
		this._queue.next({ patch, before });
	}
}

// index.html's boot script reads localStorage['theme'] (the RESOLVED-value echo, 'light'/'dark'
// only) before first paint to avoid a light→dark flash. Seeding here must NOT read that same key
// — it only ever holds a resolved value, never 'system', so echoing it back into settings.theme
// would silently overwrite a real 'system' setting with whatever it last resolved to. Instead
// this reads THEME_SETTING_KEY, which mirrors the actual theme setting (including 'system') on
// every settings change. For a signed-out/failed-auth/unavailable session where load() never
// resolves, this seed is the ONLY theme this app ever gets (the spec-required fallback), not
// just a boot flash fix — so it must preserve 'system', not collapse it.
function seedInitialSettings(): UserSettings {
	try {
		const saved = localStorage.getItem(THEME_SETTING_KEY);
		if (isTheme(saved)) {
			return { ...DEFAULT_USER_SETTINGS, theme: saved };
		}
	} catch {
		/* private mode / storage inaccessible — fall back to the default */
	}
	return DEFAULT_USER_SETTINGS;
}

function revert(before: UserSettings, patch: Partial<UserSettings>): Partial<UserSettings> {
	const out: Partial<UserSettings> = {};
	for (const key of Object.keys(patch) as (keyof UserSettings)[]) {
		(out as Record<string, unknown>)[key] = before[key];
	}
	return out;
}

export type { Theme };
